import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { AuthShell, Field, authBtn, authInput } from "@/components/portal/AuthShell";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await api.post("/auth/forgot-password", { email: email.trim() }); setDone(true); } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Reset password" subtitle="We'll email you a link to choose a new password. It works for 15 minutes." testId="forgot-page">
      {done ? <p className="border border-white/20 p-5 text-sm" data-testid="forgot-done">If an account exists for <strong>{email.trim()}</strong>, a reset link is on its way. Check your inbox.</p> : (
        <form onSubmit={submit} className="space-y-5">
          <Field label="Email address"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={authInput} data-testid="forgot-email" required /></Field>
          <button type="submit" disabled={busy || !email} className={authBtn} data-testid="forgot-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send reset link</button>
          {error && <p className="text-sm font-semibold text-[#ff6b5e]" data-testid="forgot-error">{error}</p>}
        </form>
      )}
      <Link to="/login" className="mt-6 block text-center text-sm font-semibold text-zinc-300 hover:text-white" data-testid="forgot-back">Back to log in</Link>
    </AuthShell>
  );
}

export function VerifyAccount() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ busy: false, done: null, error: "" });
  const verify = async () => {
    setState({ busy: true, done: null, error: "" });
    try { const { data } = await api.post("/auth/verify-email", { token }); setState({ busy: false, done: data, error: "" }); }
    catch (err) { setState({ busy: false, done: null, error: errText(err) }); }
  };
  return (
    <AuthShell title="Verify your email" subtitle="Confirm this new email address belongs to you to lift the temporary suspension on your account." testId="verify-email-page">
      {state.done ? (
        <div data-testid="verify-email-done">
          <p className="border border-white/20 p-5 text-sm">Thanks. <strong>{state.done.email}</strong> is verified and your account is active again.</p>
          <Link to="/login" className={`${authBtn} mt-5`} data-testid="verify-email-login">Log in</Link>
        </div>
      ) : (
        <div className="space-y-5">
          <button onClick={verify} disabled={state.busy || !token} className={authBtn} data-testid="verify-email-submit">{state.busy && <Loader2 className="h-4 w-4 animate-spin" />}Verify my email address</button>
          {(state.error || !token) && <p className="text-sm font-semibold text-[#ff6b5e]" data-testid="verify-email-error">{state.error || "This link isn't valid."}</p>}
        </div>
      )}
    </AuthShell>
  );
}

export function ReportEmailChange() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [note, setNote] = useState("");
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const send = async () => {
    setState({ busy: true, done: false, error: "" });
    try { await api.post("/auth/report-email-change", { token, note: note.trim() }); setState({ busy: false, done: true, error: "" }); }
    catch (err) { setState({ busy: false, done: false, error: errText(err) }); }
  };
  return (
    <AuthShell title="This wasn't me" subtitle="Tell us you didn't ask for your email address to be changed. Your account stays suspended while the Cresway Media team checks it, and they can change your email address back." testId="report-email-page">
      {state.done ? (
        <p className="border border-white/20 p-5 text-sm" data-testid="report-email-done">Thanks, we've received your report and the Cresway Media team will review it. Your account stays suspended for now. You'll get another email once a case file has been opened, with your case reference number. Once your email address has been changed back, we'll also email you a link to set a new password before you can log in again.</p>
      ) : (
        <div className="space-y-5">
          <Field label="Anything we should know? (optional)"><textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} className={authInput} data-testid="report-email-note" /></Field>
          <button onClick={send} disabled={state.busy || !token} className={authBtn} data-testid="report-email-submit">{state.busy && <Loader2 className="h-4 w-4 animate-spin" />}Report this change</button>
          {(state.error || !token) && <p className="text-sm font-semibold text-[#ff6b5e]" data-testid="report-email-error">{state.error || "This link isn't valid."}</p>}
        </div>
      )}
    </AuthShell>
  );
}

export function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Use at least 8 characters.");
    if (pw !== pw2) return setError("The passwords don't match.");
    setBusy(true);
    try { const { data } = await api.post("/auth/set-password", { token, password: pw }); setResult(data); } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Choose your password" subtitle="Only you will know it. Nobody else, including the administrator, can see it." testId="set-password-page">
      {result ? (
        <div data-testid="set-password-done">
          <p className="border border-white/20 p-5 text-sm">Your password is set for <strong>{result.email}</strong>.{result.still_locked ? " Your account is still suspended until the administrator reactivates it." : ""}</p>
          <Link to="/login" className={`${authBtn} mt-5`} data-testid="set-password-login">Log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <Field label="New password"><input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={authInput} data-testid="set-password-new" /></Field>
          <Field label="Confirm password"><input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={authInput} data-testid="set-password-confirm" /></Field>
          <button type="submit" disabled={busy || !token} className={authBtn} data-testid="set-password-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save password</button>
          {(error || !token) && <p className="text-sm font-semibold text-[#ff6b5e]" data-testid="set-password-error">{error || "This link isn't valid."}</p>}
        </form>
      )}
    </AuthShell>
  );
}
